import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Icon } from "../components/Icon.js";
import { PageIntro } from "../components/PageIntro.js";
import { useI18n } from "../i18n/I18nProvider.js";
import type { TranslationKey } from "../i18n/translations.js";
import {
  contactCategories,
  contactDirectoryRecords,
  filterContactRecords,
} from "./contactDirectory.js";
import type { ContactCategory, ContactDirectoryRecord } from "./contactDirectory.js";

const favoritesKey = "safesphere.contact-directory.favorites";
const categoryKeys: Readonly<Record<ContactCategory, TranslationKey>> = {
  IMMEDIATE_EMERGENCY: "directory.category.immediate",
  MEDICAL_HEALTH: "directory.category.medical",
  DISASTER_WEATHER: "directory.category.disaster",
  FIRE_RESCUE: "directory.category.fire",
  POLICE_PUBLIC_SAFETY: "directory.category.police",
  POISON_CHEMICAL: "directory.category.poison",
  UTILITIES: "directory.category.utilities",
  ROAD_TRAVEL: "directory.category.road",
  SOCIAL_SUPPORT: "directory.category.social",
  ANIMAL_PET: "directory.category.animal",
  LOCAL_REGIONAL: "directory.category.local",
};
const statusKeys: Readonly<Record<ContactDirectoryRecord["reviewStatus"], TranslationKey>> = {
  DRAFT: "directory.status.draft",
  REVIEW_REQUIRED: "directory.status.review_required",
  UNAVAILABLE: "directory.status.unavailable",
};
const evidenceKeys: Readonly<Record<ContactDirectoryRecord["evidenceStatus"], TranslationKey>> = {
  SOURCE_BOUND_DRAFT: "directory.evidence.draft",
  SCOPE_UNDER_REVIEW: "directory.evidence.review",
  LOCAL_REGIONAL_SERVICE: "directory.evidence.local",
};

function loadFavorites(): readonly string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(favoritesKey) ?? "[]");
    return Array.isArray(parsed) && parsed.every((value) => typeof value === "string")
      ? parsed
      : [];
  } catch {
    return [];
  }
}

export function ContactDirectoryPage() {
  const { locale, t } = useI18n();
  const [searchParams] = useSearchParams();
  const requestedQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(requestedQuery);
  const [category, setCategory] = useState<ContactCategory | "ALL">("ALL");
  const [selected, setSelected] = useState<ContactDirectoryRecord>();
  const [favorites, setFavorites] = useState<readonly string[]>(loadFavorites);
  const [copied, setCopied] = useState<string>();

  const records = useMemo(
    () => filterContactRecords(contactDirectoryRecords, locale, query, category),
    [category, locale, query],
  );
  const priorityRecords = records.filter((record) => record.priority <= 6);
  const otherRecords = records.filter((record) => record.priority > 6);

  useEffect(() => setSelected(undefined), [locale]);
  useEffect(() => setQuery(requestedQuery), [requestedQuery]);

  function toggleFavorite(id: string) {
    const next = favorites.includes(id)
      ? favorites.filter((value) => value !== id)
      : [...favorites, id];
    window.localStorage.setItem(favoritesKey, JSON.stringify(next));
    setFavorites(next);
  }

  async function copyNumber(record: ContactDirectoryRecord) {
    await navigator.clipboard.writeText(record.phoneNumber);
    setCopied(record.id);
  }

  function card(record: ContactDirectoryRecord, priority = false) {
    return (
      <article
        className={`directory-card${priority ? " directory-card--priority" : ""}`}
        key={record.id}
      >
        <div className="directory-card__topline">
          <span>{t(categoryKeys[record.category])}</span>
          <span
            className={`directory-evidence directory-evidence--${record.evidenceStatus.toLowerCase()}`}
          >
            {t(evidenceKeys[record.evidenceStatus])}
          </span>
          <button
            aria-label={`${favorites.includes(record.id) ? t("directory.favorite.remove") : t("directory.favorite.add")}: ${record.serviceName}`}
            aria-pressed={favorites.includes(record.id)}
            className="directory-favorite"
            onClick={() => toggleFavorite(record.id)}
            type="button"
          >
            {favorites.includes(record.id) ? "★" : "☆"}
          </button>
        </div>
        <button className="directory-card__main" onClick={() => setSelected(record)} type="button">
          <strong>{record.serviceName}</strong>
          <span className="directory-number">{record.phoneNumber}</span>
          <small>{record.purposeLabel}</small>
          <span className="directory-jurisdiction">
            <Icon name="shield" /> {record.jurisdiction}
          </span>
        </button>
        <div className="directory-card__actions">
          <button onClick={() => void copyNumber(record)} type="button">
            {copied === record.id ? t("directory.copied") : t("directory.copy")}
          </button>
          <a
            aria-label={`${t("directory.call")}: ${record.serviceName}`}
            href={`tel:${record.dialNumber}`}
          >
            {t("directory.call")}
          </a>
        </div>
      </article>
    );
  }

  return (
    <div className="page-stack directory-page">
      <PageIntro
        body={t("directory.body")}
        eyebrow={t("directory.eyebrow")}
        title={t("directory.title")}
      />
      <div className="directory-fixture-note" role="status">
        <strong>{t("directory.fixture.title")}</strong>
        <span>{t("directory.fixture.body")}</span>
      </div>
      <section aria-labelledby="directory-search-title" className="directory-controls">
        <h2 className="visually-hidden" id="directory-search-title">
          {t("directory.search")}
        </h2>
        <label>
          <span>{t("directory.search")}</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("directory.search.placeholder")}
            type="search"
            value={query}
          />
        </label>
        <div aria-label={t("directory.filters")} className="directory-filters" role="group">
          <button
            aria-pressed={category === "ALL"}
            onClick={() => setCategory("ALL")}
            type="button"
          >
            {t("directory.all")}
          </button>
          {contactCategories.map((item) => (
            <button
              aria-pressed={category === item}
              key={item}
              onClick={() => setCategory(item)}
              type="button"
            >
              {t(categoryKeys[item])}
            </button>
          ))}
        </div>
      </section>
      {records.length === 0 ? (
        <section className="directory-empty" role="status">
          <Icon name="search" />
          <h2>{t("directory.empty.title")}</h2>
          <p>{t("directory.empty.body")}</p>
        </section>
      ) : (
        <>
          {priorityRecords.length > 0 && (
            <section aria-labelledby="directory-priority-title">
              <div className="directory-heading">
                <h2 id="directory-priority-title">{t("directory.priority")}</h2>
                <span>{t("directory.tap_note")}</span>
              </div>
              <div className="directory-grid directory-grid--priority">
                {priorityRecords.map((record) => card(record, true))}
              </div>
            </section>
          )}
          {otherRecords.length > 0 && (
            <section aria-labelledby="directory-all-title">
              <div className="directory-heading">
                <h2 id="directory-all-title">{t("directory.services")}</h2>
                <span>{otherRecords.length}</span>
              </div>
              <div className="directory-grid">{otherRecords.map((record) => card(record))}</div>
            </section>
          )}
        </>
      )}
      {selected !== undefined && (
        <section aria-labelledby="directory-detail-title" className="directory-detail">
          <button
            className="directory-detail__close"
            onClick={() => setSelected(undefined)}
            type="button"
          >
            ← {t("directory.close")}
          </button>
          <p className="eyebrow">{t(categoryKeys[selected.category])}</p>
          <p
            className={`directory-evidence directory-evidence--${selected.evidenceStatus.toLowerCase()}`}
          >
            {t(evidenceKeys[selected.evidenceStatus])}
          </p>
          <h2 id="directory-detail-title">{selected.serviceName}</h2>
          <p className="directory-number">{selected.phoneNumber}</p>
          <p>{selected.purposeLabel}</p>
          <dl>
            <div>
              <dt>{t("directory.coverage")}</dt>
              <dd>{selected.jurisdiction}</dd>
            </div>
            <div>
              <dt>{t("directory.status")}</dt>
              <dd>{t(statusKeys[selected.reviewStatus])}</dd>
            </div>
            <div>
              <dt>{t("directory.reviewed")}</dt>
              <dd>{selected.lastReviewedAt ?? t("directory.unavailable")}</dd>
            </div>
            <div>
              <dt>{t("directory.retrieved")}</dt>
              <dd>{selected.sourceRetrievedAt}</dd>
            </div>
            {selected.openingHoursNote !== undefined && (
              <div>
                <dt>{t("directory.hours")}</dt>
                <dd>{selected.openingHoursNote}</dd>
              </div>
            )}
            <div>
              <dt>{t("directory.source")}</dt>
              <dd>
                {selected.sourceUrl === undefined ? (
                  selected.sourceAuthority
                ) : (
                  <a href={selected.sourceUrl} rel="noreferrer" target="_blank">
                    {selected.sourceAuthority}
                  </a>
                )}
              </dd>
            </div>
            <div>
              <dt>{t("draft.limitations")}</dt>
              <dd>
                {t(
                  selected.evidenceStatus === "SOURCE_BOUND_DRAFT"
                    ? "directory.limitations.draft"
                    : selected.evidenceStatus === "LOCAL_REGIONAL_SERVICE"
                      ? "directory.limitations.local"
                      : "directory.limitations.review",
                )}
              </dd>
            </div>
          </dl>
          <div className="directory-detail__actions">
            <button onClick={() => void copyNumber(selected)} type="button">
              {copied === selected.id ? t("directory.copied") : t("directory.copy")}
            </button>
            <a href={`tel:${selected.dialNumber}`}>{t("directory.call")}</a>
          </div>
          <p className="directory-detail__notice">{t("directory.call.notice")}</p>
        </section>
      )}
    </div>
  );
}
